import { Options, Vue } from 'vue-class-component';
import { SessionStorageEnum } from '@/utils/SessionStorageEnum';
import { appRequestsService } from '@services/app-requests.service';
import { FilterMatchMode } from 'primevue/api';
import { AppRequestStatus, PackageSize } from '@/model/AppRequest';
import moment from 'moment';
import { OrdersAppRoutesEnum } from '../../router/OrdersAppRoutesEnum';

import { AppTable, DateFormatted } from '@sharedComponents';
import { PageHeader } from '@/components';

interface AdvFilters {
  from_to?: Date[];
}

@Options({
  components: {
    AppTable,
    PageHeader,
    DateFormatted,
  },
  beforeRouteEnter(to, from, next) {
    if (from.name && !(from.name as string).startsWith('ordersapp')) {
      sessionStorage.removeItem(SessionStorageEnum.TABLE_STATE_APP_REQUESTS);
    }
    next();
  },
  beforeRouteLeave() {
    if (this.service?.cancelPendingRequests) {
      this.service.cancelPendingRequests();
    }
  },
})
export default class AppRequestsPage extends Vue {
  filters: any = null;
  advFilters: AdvFilters = {
    from_to: [
      moment().startOf('month').toDate(),
      moment().endOf('month').toDate()
    ],
  };

  get service() {
    return appRequestsService;
  }

  get stateKey() {
    return SessionStorageEnum.TABLE_STATE_APP_REQUESTS;
  }

  get statusOptions() {
    return [
      { label: 'Aperto', value: AppRequestStatus.OPEN },
      { label: 'Accettato', value: AppRequestStatus.ACCEPTED },
      { label: 'In Corso', value: AppRequestStatus.IN_PROGRESS },
      { label: 'Consegnato', value: AppRequestStatus.DELIVERED },
      { label: 'Annullato', value: AppRequestStatus.CANCELLED },
      { label: 'Scaduto', value: AppRequestStatus.EXPIRED },
    ];
  }

  get packageSizeOptions() {
    return [
      { label: 'XS', value: PackageSize.XS },
      { label: 'S', value: PackageSize.S },
      { label: 'M', value: PackageSize.M },
      { label: 'L', value: PackageSize.L },
      { label: 'XL', value: PackageSize.XL },
    ];
  }

  get table() {
    return this.$refs.dataTable as any;
  }

  formatAmount(amount: any): string {
    if (!amount) return '€ 0,00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '€ 0,00';
    return new Intl.NumberFormat('it-IT', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(numAmount);
  }

  getStatusLabel(status: AppRequestStatus): string {
    switch (status) {
      case AppRequestStatus.OPEN:
        return 'Aperto';
      case AppRequestStatus.ACCEPTED:
        return 'Accettato';
      case AppRequestStatus.IN_PROGRESS:
        return 'In Corso';
      case AppRequestStatus.DELIVERED:
        return 'Consegnato';
      case AppRequestStatus.CANCELLED:
        return 'Annullato';
      case AppRequestStatus.EXPIRED:
        return 'Scaduto';
      default:
        return status;
    }
  }

  getStatusSeverity(status: AppRequestStatus): 'success' | 'info' | 'warning' | 'danger' {
    switch (status) {
      case AppRequestStatus.OPEN:
        return 'info';
      case AppRequestStatus.ACCEPTED:
        return 'success';
      case AppRequestStatus.IN_PROGRESS:
        return 'warning';
      case AppRequestStatus.DELIVERED:
        return 'success';
      case AppRequestStatus.CANCELLED:
        return 'danger';
      case AppRequestStatus.EXPIRED:
        return 'danger';
      default:
        return 'info';
    }
  }

  getPackageSizeLabel(size: PackageSize): string {
    return size ? size.toUpperCase() : '';
  }

  onReset() {
    this.advFilters = {
      from_to: undefined,
    };
    this.updateFilters();
  }

  updateFilters() {
    if (!this.table?.filters) {
      return;
    }

    const createdAtFilter = this.table.filters.created_at;
    if (!createdAtFilter) {
      return;
    }

    const [from, to] = this.advFilters.from_to || [];

    if (from && to) {
      createdAtFilter.value = [
        moment(from).startOf('day').toDate(),
        moment(to).endOf('day').toDate(),
      ];
      createdAtFilter.matchMode = FilterMatchMode.BETWEEN;
    } else if (from) {
      createdAtFilter.value = moment(from).startOf('day').toDate();
      createdAtFilter.matchMode = 'gte';
    } else {
      createdAtFilter.value = null;
      createdAtFilter.matchMode = FilterMatchMode.CONTAINS;
    }

    // Applica i filtri
    this.table.applyFilter();
  }

  onRowClick(event: any) {
    if (event.data && event.data.id) {
      this.$router.push({
        name: OrdersAppRoutesEnum.APP_REQUEST_DETAIL,
        params: { id: event.data.id },
      });
    }
  }

  goToDetail(id: number) {
    this.$router.push({
      name: OrdersAppRoutesEnum.APP_REQUEST_DETAIL,
      params: { id },
    });
  }

  private initFilter() {
    this.filters = {
      global: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
      created_at: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
      pickup_city: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
      delivery_city: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
      package_size: {
        value: null,
        matchMode: FilterMatchMode.EQUALS,
      },
      price_offered: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
      status: {
        value: null,
        matchMode: FilterMatchMode.EQUALS,
      },
    };
  }

  created() {
    this.initFilter();
    this.$nextTick(() => {
      this.updateFilters();
    });
  }
}
